import { Button, Flex, Statistic, Typography, message } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import ReactCodeInput from "react-verification-code-input"

import { requestCode, verifyCode } from "../api/signing.services"
import type { SigningRefusal, VerifyResult } from "../types/signing.types"

const { Countdown } = Statistic
const { Text, Title } = Typography

const RESEND_AFTER_MS = 60_000

interface Props {
  session: string
  onIdentified: (verified: VerifyResult) => void
  onRefused: (refusal: SigningRefusal) => void
}

/**
 * Proving the inbox.
 *
 * The code goes to the address the document was sent to, not to whoever opened
 * the link — which is the whole reason a forwarded link cannot sign. Nothing
 * here says what that address is beyond the masked hint the API returns.
 */
const IdentityStep: React.FC<Props> = ({
  session,
  onIdentified,
  onRefused
}) => {
  const { t } = useTranslation()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [checking, setChecking] = useState(false)
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)
  const [resendAt, setResendAt] = useState<number | null>(null)

  const ask = useCallback(async () => {
    setSending(true)
    const response = await requestCode(session)
    setSending(false)

    if (!response.ok) {
      if (response.refusal?.reason === "locked_out") {
        onRefused(response.refusal)
        return
      }
      // 429 is the throttle rather than a failure: asking again in a minute
      // works, and "could not send" would have them pressing it in vain.
      void message.error(
        response.status === 429
          ? t("signing.code.tooManyRequests")
          : t("signing.code.couldNotSend")
      )
      setResendAt(Date.now() + RESEND_AFTER_MS)
      return
    }
    setSentTo(response.data?.sent_to ?? null)
    setResendAt(Date.now() + RESEND_AFTER_MS)
  }, [session, onRefused, t])

  // Once per session on arrival, not per new `t` or callback; resending afterwards is the reader's choice.
  const latestAsk = useRef(ask)
  latestAsk.current = ask
  useEffect(() => {
    void latestAsk.current()
  }, [session])

  const submit = useCallback(
    async (code: string) => {
      setChecking(true)
      const response = await verifyCode(session, code)
      setChecking(false)

      if (response.ok && response.data) {
        onIdentified(response.data)
        return
      }
      if (
        response.status === 410 ||
        response.status === 404 ||
        response.refusal?.reason === "locked_out"
      ) {
        onRefused(response.refusal ?? { reason: "unknown" })
        return
      }
      if (response.status === 429) {
        void message.error(t("signing.code.tooManyRequests"))
        return
      }
      setAttemptsLeft(response.refusal?.attempts_remaining ?? null)
    },
    [session, onIdentified, onRefused, t]
  )

  return (
    <Flex vertical gap={16}>
      <Title level={5} style={{ margin: 0 }}>
        {t("signing.code.title")}
      </Title>
      <Text type="secondary">
        {sentTo
          ? t("signing.code.sentTo", { email: sentTo })
          : t("signing.code.sending")}
      </Text>

      <Flex justify="center">
        <ReactCodeInput
          fields={6}
          type="number"
          loading={checking}
          onComplete={(code: string) => void submit(code)}
        />
      </Flex>

      {attemptsLeft !== null && (
        <Text type="danger">
          {attemptsLeft > 0
            ? t("signing.code.wrongWithAttempts", { count: attemptsLeft })
            : t("signing.code.wrongNoAttempts")}
        </Text>
      )}

      <Flex align="center" gap={8} wrap>
        <Text type="secondary">{t("signing.code.notArrived")}</Text>
        {resendAt && Date.now() < resendAt ? (
          <Countdown
            value={resendAt}
            format="mm:ss"
            valueStyle={{ fontSize: 14 }}
            onFinish={() => setResendAt(null)}
          />
        ) : (
          <Button
            type="link"
            loading={sending}
            style={{ minHeight: 44 }}
            onClick={() => void ask()}
          >
            {t("signing.code.resend")}
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default IdentityStep
